<template>
    <div class="debug-wrapper" @mouseenter="showJsonLink = true" @mouseleave="showJsonLink = false">
        <div class="debug-meta" v-if="showJsonLink">
            <span class="debug-timestamp">{{ getFormattedTimestamp() }}</span>
            <a href="#" class="show-json-link" @click.prevent="openPayloadModal">{{ $t("auto.chatbotDebugShowJson") }}</a>
        </div>
        <div class="debug-badge">
            {{ $t("auto.chatbotDebugBadge") }}
        </div>
        <div class="debug-header" :class="{ 'custom-debug': isCustomDebug() || isAIAssistantType(), 'error-debug': isErrorType(), 'warning-debug': isWarningType() }">
            <div class="debug-icon-wrapper">
                <span v-if="isCustomDebug() || isAIAssistantType()" class="material-icons">{{ getAIAssistantIcon() }}</span>
                <BlockIconPreview v-else :blockType="getBlockType()" />
            </div>
            <div class="debug-header-title">
                {{ getBlockNameFromPayload() }}
            </div>
        </div>
        
        <Modal2
            v-if="showPayloadModal"
            :title="$t('auto.chatbotDebugModalTitle')"
            :show-footer="false"
            @cancel="showPayloadModal = false"
            xlarge
        >
            <div class="payload-viewer">
                <div class="payload-section" v-if="getChatbotId() || getChatbotName() || getBoardVersion() || getStoryId()">
                    <h3>{{ $t("auto.chatbotScenarioInfoHeading") }}</h3>
                    <div class="info-grid">
                        <div class="info-item" v-if="getChatbotId()">
                            <span class="label">{{ $t("auto.labelChatbotId") }}</span>
                            <span class="value">{{ getChatbotId() }}</span>
                        </div>
                        <div class="info-item" v-if="getChatbotName()">
                            <span class="label">{{ $t("auto.labelChatbotName") }}</span>
                            <span class="value">{{ getChatbotName() }}</span>
                        </div>
                        <div class="info-item" v-if="getBoardVersion()">
                            <span class="label">{{ $t("auto.labelChatbotVersion") }}</span>
                            <span class="value">{{ getBoardVersion() }}</span>
                        </div>
                        <div class="info-item" v-if="getStoryId()">
                            <span class="label">{{ $t("auto.labelStoryId") }}</span>
                            <a 
                                :href="getStoryUrl()" 
                                target="_blank" 
                                class="value link-value"
                            >
                                {{ getStoryId() }}
                            </a>
                        </div>
                    </div>
                </div>

                <div class="payload-section" v-if="getBlockId() !== 'N/A'">
                    <h3>{{ $t("auto.blockInfoHeading") }}</h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="label">{{ $t("auto.labelTypeColon") }}</span>
                            <span class="value">{{ getBlockType() }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">{{ $t("auto.labelBlockId") }}</span>
                            <a 
                                v-if="getBlockUrl()" 
                                :href="getBlockUrl()" 
                                target="_blank" 
                                class="value link-value"
                                @click.stop
                            >
                                {{ getBlockId() }}
                            </a>
                            <span v-else class="value">{{ getBlockId() }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">{{ $t("auto.labelNameColon") }}</span>
                            <span class="value">{{ getBlockNameFromPayload() }}</span>
                        </div>
                    </div>
                </div>

                <div class="payload-section" v-if="getPayload()">
                    <h3>{{ $t("auto.payloadHeading") }}</h3>
                    <vue-json-pretty :data="getPayload()" :deep="3" />
                </div>

                <div class="payload-section" v-if="getStateInfo()">
                    <h3>{{ $t("auto.stateInfoHeading") }}</h3>
                    <vue-json-pretty :data="getStateInfo()" :deep="3" />
                </div>

                <div class="payload-section" v-if="getRuntimeInfo()">
                    <h3>{{ $t("auto.runtimeInfoHeading") }}</h3>
                    <vue-json-pretty :data="getRuntimeInfo()" :deep="3" />
                </div>

                <div class="payload-section" v-if="getChatbotInfo()">
                    <h3>{{ $t("auto.chatbotInfoHeading") }}</h3>
                    <vue-json-pretty :data="getChatbotInfo()" :deep="3" />
                </div>

                <div class="payload-section">
                    <h3>{{ $t("auto.fullPayloadRawJsonHeading") }}</h3>
                    <vue-json-pretty :data="getFullPayload()" :deep="3" />
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import Modal2 from "../../common/Modal2.vue";
import BlockIconPreview from "../../../views/Pages/Chatbot/pages/dashboard/common/BlockIconPreview.vue";
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";
import { mapGetters } from "vuex";

export default {
    name: "ChatbotDebugMessage",
    components: {
        Modal2,
        BlockIconPreview,
        VueJsonPretty,
    },
    props: {
        message: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            getStoryById: "CB/getStoryById",
        }),
        debugMode() {
            return this.$store.state.app.debugMode;
        },
    },
    data() {
        return {
            showPayloadModal: false,
            showJsonLink: false,
        };
    },
    methods: {
        isCustomDebug() {
            const type = this.$_.get(this.message, "raw.payload.type") || this.$_.get(this.message, "raw.payload.blockType");
            return type === "CUSTOM_MESSAGE" || type === "CUSTOM_DEBUG";
        },
        isAIAssistantType() {
            const type = this.$_.get(this.message, "raw.payload.type") || this.$_.get(this.message, "raw.payload.blockType");
            return type === "AI_ASSISTANT" || type === "AI_ASSISTANT_TOOL_CALL" || type === "AI_ASSISTANT_ERROR" || type === "AI_ASSISTANT_EMPTY_RESPONSE" || type === "BLOCK_ERROR";
        },
        getAIAssistantIcon() {
            const type = this.getBlockType();
            if (type === "AI_ASSISTANT") {
                return "smart_toy";
            }
            if (type === "AI_ASSISTANT_TOOL_CALL") {
                return "build";
            }
            if (type === "AI_ASSISTANT_ERROR") {
                return "error";
            }
            if (type === "AI_ASSISTANT_EMPTY_RESPONSE") {
                return "warning";
            }
            if (type === "BLOCK_ERROR") {
                return "error";
            }
            return "bug_report";
        },
        getBlockName() {
            // Önce payload'dan blockName'i kontrol et
            const blockName = this.$_.get(this.message, "raw.payload.data.blockName") || 
                             this.$_.get(this.message, "raw.payload.blockName");
            if (blockName) {
                return blockName;
            }
            
            const type = this.getBlockType();
            
            // AI Assistant tipleri için özel isimler
            if (type === "AI_ASSISTANT") {
                const isTriggered = this.$_.get(this.message, "raw.payload.data.triggered", false);
                if (isTriggered) {
                    return "AI Assistant Triggered";
                }
                return "AI Assistant Response";
            }
            if (type === "AI_ASSISTANT_TOOL_CALL") {
                const functionName = this.$_.get(this.message, "raw.payload.data.name") || 
                                     this.$_.get(this.message, "raw.payload.data.functionCall.name");
                return functionName ? `AI Assistant Tool Call: ${functionName}` : "AI Assistant Tool Call";
            }
            if (type === "AI_ASSISTANT_ERROR") {
                return "AI Assistant Error";
            }
            if (type === "AI_ASSISTANT_EMPTY_RESPONSE") {
                return "AI Assistant Empty Response";
            }
            if (type === "BLOCK_ERROR") {
                const blockType = this.$_.get(this.message, "raw.payload.data.blockType", "");
                return blockType ? `Block Error (${blockType})` : "Block Error";
            }
            
            if (this.isCustomDebug()) {
                return this.$_.get(this.message, "raw.payload.title") || 
                       this.$_.get(this.message, "raw.payload.name") || 
                       "Custom Debug";
            }
            
            // Son çare olarak type'ı göster
            return type !== "UNKNOWN" ? type : "Unnamed Block";
        },
        getBlockType() {
            return this.$_.get(this.message, "raw.payload.type") || 
                   this.$_.get(this.message, "raw.payload.blockType", "UNKNOWN");
        },
        getBlockId() {
            const blockId = this.$_.get(this.message, "raw.payload.data.blockId") || 
                           this.$_.get(this.message, "raw.payload.blockId");
            return blockId || "N/A";
        },
        getBlockNameFromPayload() {
            // getBlockName() zaten blockName'i kontrol ediyor
            return this.getBlockName();
        },
        getStoryId() {
            return this.$_.get(this.message, "raw.payload.chatbot.storyId");
        },
        getBoardVersion() {
            return this.$_.get(this.message, "raw.payload.chatbot.boardVersion");
        },
        getChatbotId() {
            return this.$_.get(this.message, "raw.payload.chatbot.id");
        },
        getChatbotName() {
            const storyId = this.getStoryId();
            if (!storyId) return null;
            const story = this.getStoryById(storyId);
            return story ? story.title : null;
        },
        getStoryUrl() {
            const storyId = this.getStoryId();
            if (!storyId) return null;
            const baseUrl = window.location.origin;
            const version = this.getBoardVersion();
            if (!version) return null;
            return `${baseUrl}/chatbot/story/${storyId}/${version}`;
        },
        getBlockUrl() {
            const storyId = this.getStoryId();
            const blockId = this.getBlockId();
            const version = this.getBoardVersion();
            
            // Tüm değerlerin varlığını kontrol et
            if (!storyId || !blockId || blockId === "N/A" || blockId === null || blockId === undefined) {
                return null;
            }
            if (!version || version === null || version === undefined) {
                return null;
            }
            
            const baseUrl = window.location.origin;
            return `${baseUrl}/chatbot/story/${storyId}/${version}/block/${blockId}`;
        },
        getTimestamp() {
            const timestamp = this.$_.get(this.message, "raw.payload.timestamp");
            return timestamp ? this.$moment(timestamp).format("HH:mm:ss") : "";
        },
        getFormattedTimestamp() {
            const timestamp = this.$_.get(this.message, "raw.payload.timestamp");
            if (!timestamp) return "";
            const now = this.$moment();
            const msgTime = this.$moment(timestamp);
            
            if (now.isSame(msgTime, "day")) {
                return `Today ${msgTime.format("h:mm A")}`;
            } else if (now.clone().subtract(1, "day").isSame(msgTime, "day")) {
                return `Yesterday ${msgTime.format("h:mm A")}`;
            } else {
                return msgTime.format("MMM D, h:mm A");
            }
        },
        getFullTimestamp() {
            const timestamp = this.$_.get(this.message, "raw.payload.timestamp");
            return timestamp ? this.$moment(timestamp).format("DD.MM.YYYY HH:mm:ss") : "";
        },
        getPayload() {
            // Payload'ı döndür - data field'ından al
            const data = this.$_.get(this.message, "raw.payload.data", null);
            if (data) {
                return data;
            }
            // Eski format için uyumluluk - blockDetails varsa onu döndür
            return this.$_.get(this.message, "raw.payload.blockDetails", null);
        },
        getStateInfo() {
            return this.$_.get(this.message, "raw.payload.state", null);
        },
        getChatbotInfo() {
            return this.$_.get(this.message, "raw.payload.chatbot", null);
        },
        getRuntimeInfo() {
            var runtime = this.$_.get(this.message, "raw.payload.runtime", null);
            if (!runtime) return null;
            if (!this.debugMode) {
                return this.$_.omit(runtime, ["pid"]);
            }
            return runtime;
        },
        getPid() {
            return this.$_.get(this.message, "raw.payload.runtime.pid", null);
        },
        isErrorType() {
            const type = this.getBlockType();
            return type === "AI_ASSISTANT_ERROR" || type === "BLOCK_ERROR";
        },
        isWarningType() {
            const type = this.getBlockType();
            return type === "AI_ASSISTANT_EMPTY_RESPONSE";
        },
        getFullPayload() {
            return this.$_.get(this.message, "raw.payload", {});
        },
        openPayloadModal() {
            this.showPayloadModal = true;
        }
    }
};
</script>

<style lang="scss">
.debug-wrapper {
    border: 1px solid #dbe1e8;
    border-radius: 7px;
    max-width: 330px;
    overflow: visible;
    padding: 20px;
    position: relative;
    width: 100%;
    background: #fff;
    margin: 8px 0;
    
    .debug-meta {
        position: absolute;
        left: -2px;
        top: -37px;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: #f5f7fa;
        border-radius: 6px;
        white-space: nowrap;
        z-index: 10;
        
        .debug-timestamp {
            font-size: 13px;
            color: #6b7280;
        }
        
        .show-json-link {
            font-size: 13px;
            color: #006cff;
            text-decoration: none;
            font-weight: 500;
            cursor: pointer;
            
            &:hover {
                text-decoration: underline;
            }
        }
    }
    
    .debug-badge {
        border: 1px solid #dbe1e8;
        background: #fff;
        color: #006cff;
        border-bottom-left-radius: 8px;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        opacity: 0.7;
        padding: 4px 12px;
        position: absolute;
        right: -1px;
        top: -1px;
    }
    
    .debug-header {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .debug-icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            background: #1e3a5f;
            
            .material-icons {
                color: #ffffff !important;
                font-size: 20px;
            }
            
            .custom-block-logo {
                width: 24px;
                height: 24px;
                filter: brightness(0) invert(1); // Beyaz yap
            }
            
            img.custom-block-logo {
                filter: brightness(0) invert(1); // Beyaz yap
            }
            
            svg {
                fill: #ffffff !important;
                color: #ffffff !important;
            }
        }
        
        &.custom-debug {
            .debug-icon-wrapper {
                background: #1e3a5f;
                
                .material-icons {
                    color: #ffffff !important;
                }
            }
        }
        
        &.error-debug {
            .debug-icon-wrapper {
                background: #dc2626;
            }
        }
        
        &.warning-debug {
            .debug-icon-wrapper {
                background: #d97706;
            }
        }
        
        .debug-header-title {
            font-size: 16px;
            font-weight: 600;
            line-height: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: #1f2937;
            
            .debug-pid {
                font-size: 11px;
                font-weight: 400;
                color: #9ca3af;
                margin-left: 6px;
            }
        }
    }
}

.payload-viewer {
    max-height: 70vh;
    overflow-y: auto;
    
    .payload-section {
        margin-bottom: 24px;
        
        &:last-child {
            margin-bottom: 0;
        }
        
        h3 {
            font-size: 16px;
            font-weight: 700;
            color: #1f2937;
            margin: 0 0 12px 0;
            padding-bottom: 8px;
            border-bottom: 2px solid #e5e7eb;
        }
        
        .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 12px;
            
            .info-item {
                background: #f9fafb;
                padding: 8px 12px;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
                
                .label {
                    font-weight: 600;
                    color: #6b7280;
                    font-size: 12px;
                    display: block;
                    margin-bottom: 4px;
                }
                
                .value {
                    color: #1f2937;
                    font-size: 14px;
                    word-break: break-all;
                    
                    &.link-value {
                        color: #006cff;
                        text-decoration: none;
                        cursor: pointer;
                        transition: color 0.2s;
                        
                        &:hover {
                            color: #0052cc;
                            text-decoration: underline;
                        }
                    }
                }
            }
        }
        
        :deep(.vjs-tree) {
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            padding: 16px;
            font-family: 'Courier New', Courier, monospace;
            font-size: 13px;
            max-height: 500px;
            overflow-y: auto;
        }
        
        :deep(.vjs-tree-node) {
            color: #1f2937;
        }
        
        :deep(.vjs-key) {
            color: #0066cc;
            font-weight: 600;
        }
        
        :deep(.vjs-value-string) {
            color: #008000;
        }
        
        :deep(.vjs-value-number) {
            color: #ff6600;
        }
        
        :deep(.vjs-value-boolean) {
            color: #cc0000;
        }
        
        :deep(.vjs-value-null) {
            color: #999999;
        }
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .debug-wrapper {
        background: #1f2937;
        border-color: #374151;
        
        .debug-meta {
            background: #111827;
            
            .debug-timestamp {
                color: #9ca3af;
            }
            
            .show-json-link {
                color: #60a5fa;
            }
        }
        
        .debug-badge {
            background: #1f2937;
            border-color: #374151;
            color: #60a5fa;
        }
        
        .debug-header {
            .debug-header-title {
                color: #f9fafb;
            }
        }
    }
    
    .payload-viewer {
        .payload-section {
            h3 {
                color: #f9fafb;
                border-bottom-color: #374151;
            }
            
            .info-grid {
                .info-item {
                    background: #111827;
                    border-color: #374151;
                    
                    .label {
                        color: #9ca3af;
                    }
                    
                    .value {
                        color: #f9fafb;
                        
                        &.link-value {
                            color: #60a5fa;
                            
                            &:hover {
                                color: #93c5fd;
                            }
                        }
                    }
                }
            }
            
            :deep(.vjs-tree) {
                background: #111827;
                border-color: #374151;
            }
            
            :deep(.vjs-tree-node) {
                color: #f9fafb;
            }
            
            :deep(.vjs-key) {
                color: #60a5fa;
            }
            
            :deep(.vjs-value-string) {
                color: #34d399;
            }
            
            :deep(.vjs-value-number) {
                color: #fbbf24;
            }
            
            :deep(.vjs-value-boolean) {
                color: #f87171;
            }
            
            :deep(.vjs-value-null) {
                color: #9ca3af;
            }
        }
    }
}
</style>
